import React, { useState, useEffect } from 'react';
import { getSocietyWiseOverview, SocietyPromotionOverview } from '../services/api';
import './AdminSocietyOverview.css';

const AdminSocietyOverview: React.FC = () => {
  const [data, setData] = useState<SocietyPromotionOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await getSocietyWiseOverview();
      setData(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load society overview');
    } finally {
      setLoading(false);
    }
  };

  // Group data by society
  const groupedBySociety = data.reduce((acc, item) => {
    if (!acc[item.society_id]) {
      acc[item.society_id] = {
        society_id: item.society_id,
        society_name: item.society_name,
        promotions: [] as SocietyPromotionOverview[],
      };
    }
    // Only add promotions that exist (promotion_id is not null)
    if (item.promotion_id) {
      acc[item.society_id].promotions.push(item);
    }
    return acc;
  }, {} as Record<number, { society_id: number; society_name: string; promotions: SocietyPromotionOverview[] }>);

  const societies = Object.values(groupedBySociety);

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      story: 'Story',
      spotlight: 'Spotlight',
      category: 'Category',
    };
    return labels[type] || type;
  };

  const getTypeBadgeClass = (type: string) => {
    const classes: { [key: string]: string } = {
      story: 'type-story',
      spotlight: 'type-spotlight',
      category: 'type-category',
    };
    return classes[type] || '';
  };

  if (loading) {
    return <div className="loading">Loading society overview...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="admin-society-overview">
      <div className="overview-header">
        <h2>Society-Wise Promotion Overview</h2>
        <p className="subtitle">View all active advertisements running on each society</p>
      </div>

      {societies.length === 0 ? (
        <div className="empty-state">
          <p>No societies found with active promotions.</p>
        </div>
      ) : (
        <div className="societies-grid">
          {societies.map((society) => (
            <div key={society.society_id} className="society-card">
              <div className="society-card-header">
                <h3>{society.society_name}</h3>
                <span className="promotion-count-badge">
                  {society.promotions.length} {society.promotions.length === 1 ? 'Promotion' : 'Promotions'}
                </span>
              </div>

              {society.promotions.length === 0 ? (
                <div className="no-promotions">
                  <p>No active promotions for this society</p>
                </div>
              ) : (
                <div className="promotions-list">
                  {society.promotions.map((promo) => (
                    <div key={promo.promotion_id} className="promotion-item">
                      <div className="promotion-header">
                        <h4>{promo.promotion_title}</h4>
                        <span className={`type-badge ${getTypeBadgeClass(promo.promotion_type)}`}>
                          {getTypeLabel(promo.promotion_type)}
                        </span>
                      </div>

                      <div className="promotion-details">
                        {promo.brand_name && (
                          <div className="detail-item">
                            <span className="detail-label">Brand:</span>
                            <span className="detail-value">{promo.brand_name}</span>
                          </div>
                        )}

                        {(promo.discount_percentage || promo.discount_amount) && (
                          <div className="detail-item">
                            <span className="detail-label">Discount:</span>
                            <span className="detail-value discount">
                              {promo.discount_percentage
                                ? `${promo.discount_percentage}%`
                                : promo.discount_amount
                                ? `₹${promo.discount_amount}`
                                : '-'}
                            </span>
                          </div>
                        )}

                        {promo.coupon_code && (
                          <div className="detail-item">
                            <span className="detail-label">Coupon:</span>
                            <span className="detail-value coupon-code">{promo.coupon_code}</span>
                          </div>
                        )}

                        <div className="detail-item">
                          <span className="detail-label">Valid:</span>
                          <span className="detail-value">
                            {new Date(promo.valid_from).toLocaleDateString()} - {new Date(promo.valid_to).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSocietyOverview;

